import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const LibraryReturn = ({ onNavigate }) => {
    const [bookId, setBookId] = useState('');
    const [returnPreview, setReturnPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const handleSearch = async () => {
        if (!bookId.trim()) {
            setError('Please enter a valid Accession No. or ISBN.');
            return;
        }

        setLoading(true);
        setError(null);
        setReturnPreview(null);
        setSuccessMessage('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_LINK}/api/library/issues/search?q=${encodeURIComponent(bookId.trim())}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const result = await response.json();

            if (result.success) {
                const { issue, fine } = result.data;
                // Format dates safely
                const formatDate = (dateString) => {
                    if (!dateString) return 'N/A';
                    const date = new Date(dateString);
                    return isNaN(date.getTime()) ? 'Invalid Date' : date.toISOString().split('T')[0];
                };

                setReturnPreview({
                    id: issue.id,
                    book: issue.book_title,
                    student: issue.member_name,
                    issueDate: formatDate(issue.issue_date),
                    dueDate: formatDate(issue.due_date),
                    daysOverdue: Math.max(0, issue.days_overdue || 0),
                    fine: fine || 0
                });
            } else {
                setError(result.message || 'Book not found or not issued.');
            }
        } catch (err) {
            console.error('Error searching book:', err);
            setError('Failed to search book. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleReturn = async () => {
        if (!returnPreview) return;

        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_LINK}/api/library/return`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    issue_id: returnPreview.id,
                    fine_amount: returnPreview.fine
                })
            });

            const result = await response.json();

            if (result.success) {
                setSuccessMessage('Book returned successfully!');
                setReturnPreview(null);
                setBookId('');

                // Clear success message after 3 seconds
                setTimeout(() => {
                    setSuccessMessage('');
                }, 3000);
            } else {
                setError(result.message || 'Failed to return book.');
            }
        } catch (err) {
            console.error('Error returning book:', err);
            setError('Failed to return book. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout role="librarian" onNavigate={onNavigate} activePage="library-return" pageTitle="Return Books">
            <div style={{ padding: '20px' }}>
                <h2 style={{ color: '#333', marginBottom: '2rem' }}>🔄 Return Books</h2>

                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    {/* Error & Success Messages */}
                    {error && (
                        <div style={{ backgroundColor: '#ffeef0', color: '#e53935', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ffccd5' }}>
                            <strong>❌ Error:</strong> {error}
                        </div>
                    )}
                    {successMessage && (
                        <div style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #c8e6c9' }}>
                            <strong>✅ Success:</strong> {successMessage}
                        </div>
                    )}

                    {/* Search Section */}
                    <div style={{
                        backgroundColor: 'white',
                        padding: '2rem',
                        borderRadius: '12px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                        marginBottom: '2rem'
                    }}>
                        <h3 style={{ marginTop: 0, color: '#238548', marginBottom: '1.5rem' }}>Scan or Enter Book to Return</h3>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <input
                                type="text"
                                placeholder="Enter Book ISBN / Accession No."
                                value={bookId}
                                onChange={e => setBookId(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSearch();
                                    }
                                }}
                                style={{ flex: 1, padding: '15px', border: '2px solid #ddd', borderRadius: '8px', fontSize: '1.1rem' }}
                            />
                            <button
                                onClick={handleSearch}
                                disabled={loading || !bookId.trim()}
                                style={{
                                    backgroundColor: loading || !bookId.trim() ? '#ccc' : '#238548',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0 30px',
                                    cursor: loading || !bookId.trim() ? 'not-allowed' : 'pointer',
                                    fontSize: '1.1rem',
                                    fontWeight: '600'
                                }}
                            >
                                {loading ? 'Searching...' : 'Find'}
                            </button>
                        </div>
                    </div>

                    {/* Return Details Preview */}
                    {returnPreview && (
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                            overflow: 'hidden',
                            animation: 'fadeIn 0.5s ease'
                        }}>
                            <div style={{ padding: '1.5rem', backgroundColor: '#f8f9fa', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ margin: 0, color: '#333' }}>Return Details</h3>
                                {returnPreview.daysOverdue > 0 && (
                                    <span style={{ backgroundColor: '#f44336', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600' }}>Overdue</span>
                                )}
                            </div>

                            <div style={{ padding: '2rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
                                    <div>
                                        <label style={{ color: '#666', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>Book Title</label>
                                        <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{returnPreview.book}</div>
                                    </div>
                                    <div>
                                        <label style={{ color: '#666', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>Issued To</label>
                                        <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{returnPreview.student}</div>
                                    </div>
                                    <div>
                                        <label style={{ color: '#666', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>Due Date</label>
                                        <div style={{ fontWeight: '500' }}>{returnPreview.dueDate}</div>
                                    </div>
                                    <div>
                                        <label style={{ color: '#666', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>Days Overdue</label>
                                        <div style={{ fontWeight: '600', color: returnPreview.daysOverdue > 0 ? '#f44336' : '#238548' }}>
                                            {returnPreview.daysOverdue} Days
                                        </div>
                                    </div>
                                </div>

                                {returnPreview.fine > 0 && (
                                    <div style={{ backgroundColor: '#ffebee', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ color: '#c62828', fontWeight: '500' }}>Fine Applicable:</div>
                                        <div style={{ color: '#c62828', fontWeight: 'bold', fontSize: '1.5rem' }}>₹{returnPreview.fine}</div>
                                    </div>
                                )}
                                {returnPreview.fine === 0 && (
                                    <div style={{ backgroundColor: '#e8f5e9', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ color: '#2e7d32', fontWeight: '500' }}>No Fine Applicable</div>
                                        <div style={{ color: '#2e7d32', fontWeight: 'bold', fontSize: '1.5rem' }}>₹0</div>
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <button
                                        onClick={handleReturn}
                                        disabled={loading}
                                        style={{
                                            flex: 1,
                                            backgroundColor: loading ? '#ccc' : '#238548',
                                            color: 'white',
                                            border: 'none',
                                            padding: '15px',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            cursor: loading ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {loading ? 'Processing...' : (returnPreview.fine > 0 ? 'Confirm Return & Collect Fine' : 'Confirm Return')}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setReturnPreview(null);
                                            setBookId('');
                                        }}
                                        disabled={loading}
                                        style={{
                                            border: '2px solid #f44336',
                                            backgroundColor: 'white',
                                            color: '#f44336',
                                            padding: '15px 25px',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            cursor: loading ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default LibraryReturn;
